import { apiFetch } from './api-rest-provider';
import { captureSubject } from '../utils/capture-session';
import type { Permissions } from '../types';

export * from './configuration-types';
import type { ActiveConfigurations, ConfigVersion, ConfigurationState, DraftInput, ValidationResult } from './configuration-types';

const pathFor = (id: string) => `configurations/${encodeURIComponent(id)}`;
export const configurations = {
  list: (program: string) => apiFetch(`configurations?program_id=${encodeURIComponent(program)}`) as Promise<{ items: ConfigVersion[] }>,
  active: (program: string) => apiFetch(`configurations/active?program_id=${encodeURIComponent(program)}`) as Promise<ActiveConfigurations>,
  create: (draft: DraftInput) => apiFetch('configurations', { method: 'POST', body: JSON.stringify(draft) }) as Promise<ConfigVersion>,
  update: (version: ConfigVersion, draft: DraftInput) => {
    const { program_id: _program, ...editable } = draft;
    return apiFetch(pathFor(version.id), { method: 'PUT', body: JSON.stringify({ ...editable, expected_version: version.record_version }) }) as Promise<ConfigVersion>;
  },
  validate: (id: string) => apiFetch(`${pathFor(id)}/validate`, { method: 'POST', body: '{}' }) as Promise<ValidationResult>,
  transition: (version: ConfigVersion, state: ConfigurationState) => apiFetch(`${pathFor(version.id)}/transition`, { method: 'POST', body: JSON.stringify({ expected_version: version.record_version, state }) }) as Promise<ConfigVersion>,
  clone: (id: string) => apiFetch(`${pathFor(id)}/clone`, { method: 'POST', body: '{}' }) as Promise<ConfigVersion>,
};

/** Display gate only. The API authorizes every action and enforces independent approval. */
export function configurationRights(permissions: Permissions | undefined, orgPath: string) {
  const assignments = permissions?.assignments.filter(a => a.orgPath && orgPath &&
    (a.orgPath === '/' || a.orgPath === orgPath || orgPath.startsWith(`${a.orgPath.replace(/\/$/, '')}/`))) ?? [];
  return {
    edit: assignments.some(a => ['cadre_manager', 'instructor', 'course_director', 'purchasing_officer', 'echelon_commander', 'enterprise_leader', 'tenant_admin'].includes(a.role)),
    publish: assignments.some(a => ['course_director', 'tenant_admin'].includes(a.role)),
  };
}

interface Snapshot { response: ActiveConfigurations; revision: number; actor: string; synchronizedAt: string }
/** Warm-session memory only. No browser disk cache or disconnected authorization is created. */
const snapshots = new Map<string, Snapshot>();
export function clearPublishedSnapshots(): void { snapshots.clear(); }
export function rememberPublished(response: ActiveConfigurations, token: string | null, revision: number): void {
  const actor = captureSubject(token);
  if (!actor || response.actorId !== actor || !response.tenantId || !response.program?.id || !response.program.orgPath) throw new Error('Configuration identity could not be verified');
  if (response.items.some(item => item.program_id !== response.program.id || item.state !== 'published' || !item.digest)) throw new Error('Invalid published configuration snapshot');
  // A changed session discards the previous session's definitions and identity.
  for (const [key, value] of snapshots) if (value.revision !== revision || value.actor !== actor || value.response.program.id === response.program.id) snapshots.delete(key);
  const key = JSON.stringify([response.tenantId, actor, response.program.id]);
  snapshots.set(key, { response: structuredClone(response), revision, actor, synchronizedAt: new Date().toISOString() });
}
export function recalledPublished(program: string, token: string | null, revision: number, now = Date.now()): Snapshot | undefined {
  const actor = captureSubject(token, now);
  for (const [key, value] of snapshots) {
    if (!actor || value.revision !== revision || value.actor !== actor) { snapshots.delete(key); continue; }
    if (value.response.program.id === program) {
      const copy = structuredClone(value);
      copy.response.items = copy.response.items.filter(item => Date.parse(item.effective_from) <= now &&
        (!item.effective_until || Date.parse(item.effective_until) > now));
      return copy;
    }
  }
  return undefined;
}
